import { type MatrixEvent, MsgType, RelationType } from "matrix-js-sdk/src/matrix";

import { type Preview } from "./Preview";
import { type TagID } from "../../room-list-v3/skip-list/tag";
import { _t, sanitizeForTranslation } from "../../../languageHandler";
import { getSenderName, isSelf, shouldPrefixMessagesIn } from "./utils";
import { getHtmlText } from "../../../HtmlUtils";
import { stripHTMLReply, stripPlainReply } from "../../../utils/Reply";

export class MessageEventPreview implements Preview {
    public getTextFor(event: MatrixEvent, tagId?: TagID, isThread?: boolean): string | null {
        let eventContent = event.getContent();

        if (event.isRelation(RelationType.Replace)) {
            // It's an edit, generate the preview on the new text
            eventContent = event.getContent()["m.new_content"];
        }

        if (!eventContent?.["body"]) return null; // invalid for our purposes

        let body = eventContent["body"].trim();
        if (!body) return null; // invalid event, no preview
        // A msgtype is actually required in the spec but the app is a bit softer on this requirement
        const msgtype = eventContent["msgtype"] ?? MsgType.Text;

        const hasHtml = eventContent.format === "org.matrix.custom.html" && eventContent.formatted_body;
        if (hasHtml) {
            body = eventContent.formatted_body;
        }

        // XXX: Newer relations have a getRelation() function which is not compatible with replies.
        if (event.getWireContent()["m.relates_to"]?.["m.in_reply_to"]) {
            // If this is a reply, get the real reply and use that
            if (hasHtml) {
                body = (stripHTMLReply(body) || "").trim();
            } else {
                body = (stripPlainReply(body) || "").trim();
            }
            if (!body) return null; // invalid event, no preview
        }

        if (hasHtml) {
            const sanitised = getHtmlText(body.replace(/<br\/?>/gi, "\n")); // replace line breaks before removing them
            // run it through DOMParser to fixup encoded html entities
            body = new DOMParser().parseFromString(sanitised, "text/html").documentElement.textContent;
        }

        body = sanitizeForTranslation(body);

        const roomId: string | undefined = event.getRoomId();

        // if (isThread || isSelf(event) || (roomId && !shouldPrefixMessagesIn(roomId, tagId))) {
        //     return body;
        // } else {
        //     return _t("event_preview.m_text", { senderName: getSenderName(event), message: body });
        // }

        // DTH: Chat 1:1
        // Se è un messaggio proprio, mostra "Tu: messaggio"
        // Se è un messaggio ricevuto, mostra solo "messaggio" senza prefisso del mittente
        // DTH: Chat gruppo
        // Se è un messaggio proprio, mostra "Tu: messaggio"
        // Se è un messaggio ricevuto, mostra "NomeMittente: messaggio"
        const isSelfMessage = isSelf(event);
        const sender: string = isSelfMessage ?
            _t("event_preview.m_text_you") :
            getSenderName(event);
        // DTH: Mostra il prefisso del mittente se è un messaggio proprio o se la stanza non è una chat 1:1. 
        // Per i messaggi ricevuti in una chat 1:1, non mostrare il prefisso.
        const showSender: boolean = isSelfMessage || (!!roomId && shouldPrefixMessagesIn(roomId, tagId));

        let message: string = body;
        switch (msgtype) {
            case MsgType.Text:
                message = body
                break;
            case MsgType.Image:
                message = `${_t("common.image")} ${body}`;
                break;
            case MsgType.Audio:
                message = `${_t("common.voice_message")}`;
                break;
            case MsgType.Video:
                message = `${_t("common.video_message")} ${body}`;
                break;
            case MsgType.File:
                message = `${_t("common.file")} ${body}`;
                break;
            case MsgType.Location:
                message = `${_t("common.location")} ${body}`;
                break;
            case MsgType.Emote:
                // Per l'emote forziamo il mittente a prescindere
                return _t("event_preview.m_emote", { senderName: sender, emote: body });
            case "m.bad.encrypted":
                // Testo da mostrare in caso di errore cifratura messaggi
                message = `** ${_t("timeline.decryption_failure.historical_event_unverified_device_custom")} **`;
                break;
            default:
                message = body;
                break;
        }

        // DTH: Mostra il prefisso del mittente se è true, altrimenti solo il messaggio
        return showSender ?
            _t("event_preview.m_text", { senderName: sender, message }) :
            message;
    }
}
